import { CopyOutlined } from '@ant-design/icons';
import {
  CampoSemantico,
  EnumRichiestaDTOStato,
  GetFrontRichiesteParams,
  getGetFrontRichiesteQueryKey,
  GetTagSemanticiTipoSezione,
  RichiestaDTO,
  useDeleteFrontRichiesteId,
  useGetFrontRichieste,
  useGetFrontTipiDomanda,
} from '@framework/api-client';
import { getNetworkErrorMessage } from '@framework/auth';
import {
  ExportableFileTypes,
  TitleSubmissionRow,
  useSemanticTagSearchableExtension,
} from '@framework/common-web';
import { SubmissionLogic } from '@framework/frontoffice-submission';
import { useTranslation } from '@framework/i18n';
import {
  AdvancedSearch,
  BrandedPath,
  ColumnSortDirections,
  FilterButton,
  FrontFilterPanel,
  FrontMessageFilteredList,
  getPagination,
  getPaginationUrlColumns,
  handlePaginationChange,
  ResponsiveCard,
  TableData,
  useBreadcrumbItem,
  useHistory,
  usePaginationUrlQueryState,
} from '@framework/ui';
import { Button, Divider, message, Space, Tooltip } from 'antd';
import { isEqual, omit } from 'lodash';
import { useMemo, useState } from 'react';
import { RouteComponentProps } from 'react-router';
import { down } from 'styled-breakpoints';
import { useBreakpoint } from 'styled-breakpoints/react-styled';
import { queryClient } from '../../core/network/queryClient';
import { FilterDrawerButton } from '../../ui/drawer/FilterDrawerButton';
import { PageLayout } from '../../ui/layout/PageLayout';
import { NetworkAlertPage } from '../errors/network-alert/NetworkAlertPage';
import { LoadingPage } from '../loading/LoadingPage';
import { ArchivedSubmissionsLinkButton } from './archive/ArchivedSubmissionsLinkButton';
import { ExportSubmissionFileButton } from './components/file-export/ExportSubmissionFileButton';
import { FrontofficeExportFileLogic } from './components/file-export/FrontofficeExportFileLogic';
import { SubmissionDeleteButton } from './components/SubmissionDeleteButton';
import { SubmissionCloneButton } from './components/SubmissionDuplicateButton';
import { SubmissionViewTableButton } from './components/SubmissionViewTableButton';
import { useSubmissionSearchableFields } from './filter/useSubmissionSearchableFields';
import { GetFrontExtendedFiltersSchema } from './schema/GetFrontExtendedFiltersSchema';
import {
  submissionsColumns,
  submissionsColumnsMobile,
} from './submissionsColumns';

const PAGE_TITLE_ID = 'submission-list-title-focus-id';
const returnFocusToPageTitle = () => {
  const titleElement = document.getElementById(PAGE_TITLE_ID);
  titleElement?.focus();
};

export interface ISubmissionListPageProps extends RouteComponentProps {}

export function SubmissionListPage(props: ISubmissionListPageProps) {
  const history = useHistory();
  // Pannello Filtro (Desktop)
  const [openSearchPanel, setOpenSearchPanel] = useState(false);
  // Filtro Mobile (Drawer)
  const [openFilterDrawer, setOpenFilterDrawer] = useState(false);
  const isMobile = useBreakpoint(down('md'));
  const { t } = useTranslation();

  const defaultQuery = useMemo(
    () =>
      getPagination({
        page: 0,
        pageSize: 10,
        sort: 'dataModifica',
        direction: ColumnSortDirections.descend,
      }),
    [],
  );
  const { data: rawInquiries } = useGetFrontTipiDomanda();
  const inquiries = rawInquiries?.dati ?? [];

  useBreadcrumbItem({
    path: props.location.pathname as BrandedPath,
    title: t('submission-list.richieste', 'Richieste'),
  });

  const [query, setQuery] = usePaginationUrlQueryState<GetFrontRichiesteParams>(
    {
      history,
      input: defaultQuery,
      // Non richiesto. Se presente, salva la query in locale
      // persistKey: 'frontoffice-submission-list',
    },
  );

  const { data, error, isLoading, isFetching } = useGetFrontRichieste(query, {
    query: { keepPreviousData: true },
    request: { arrayFormat: 'repeat' },
  });

  const submissions = data?.dati;
  const meta = data?.meta;

  const defaultColumns = isMobile
    ? submissionsColumnsMobile
    : submissionsColumns;

  // Crea un array di "campiSemantici" rimuovendo tutti gli elementi non definiti
  const semanticFieldList = useMemo(
    () =>
      submissions
        ?.map(s => s.campiSemantici)
        .flat()
        .filter((c): c is CampoSemantico => Boolean(c)) ?? [],

    [submissions],
  );

  const semanticTagSearchableOptions = useSemanticTagSearchableExtension({
    viewSemanticColumns: Boolean(query.idGruppoTipoDomanda),
    sectionType: GetTagSemanticiTipoSezione.FRONTOFFICE,
    // procedureTypeId: query.tipo,
    semanticFields: semanticFieldList,
  });

  const searchableFields = useSubmissionSearchableFields({
    extraFields: semanticTagSearchableOptions.searchableFields,
  });

  const columns = getPaginationUrlColumns<
    RichiestaDTO,
    GetFrontRichiesteParams
  >([...defaultColumns, ...semanticTagSearchableOptions.columns], query, {
    progressivo: ['progressivoId', 'progressivoAnno'],
  });

  const onPaginationChange = handlePaginationChange<
    RichiestaDTO,
    GetFrontRichiesteParams
  >({
    columns,
    defaultQuery,
    query,
    setQuery,
    // Non ci sono filtri in tabella, sono gestiti tutti dall'avdvanced search
    getFilters: filters => ({}),
  });

  const deleteMutation = useDeleteFrontRichiesteId({
    mutation: {
      onSuccess: () => {
        message.success(
          t(
            'submission-list.richiesta-eliminata-con-successo',
            'Richiesta eliminata con successo',
          ),
        );
        queryClient.invalidateQueries(getGetFrontRichiesteQueryKey());
      },
      onError: error => {
        message.error(
          getNetworkErrorMessage(
            error,
            t(
              'submission-list.errore-eliminazione-richiesta',
              "Errore durante l'eliminazione della richiesta",
            ),
          ),
        );
      },
    },
  });

  const isFiltered = useMemo(() => {
    if (isFetching) return false;
    return !isEqual(
      omit(query, ['pagina', 'ordinamenti', 'limite']),
      omit(defaultQuery, ['pagina', 'ordinamenti', 'limite']),
    );
  }, [isFetching, query, defaultQuery]);

  const handleDelete = (submission: RichiestaDTO) => {
    deleteMutation.mutateAsync({ id: submission.id! }).catch(err => {
      console.error(err);
    });
  };

  if (isLoading ?? isFetching) return <LoadingPage />;

  if (error) {
    return (
      <NetworkAlertPage
        message={t(
          'submission-list.impossibile-caricare-elenco-richieste',
          "Impossibile caricare l'elenco delle Richieste",
        )}
        error={error}
      />
    );
  }

  const filterform = (
    <AdvancedSearch
      id="submission-list"
      schema={GetFrontExtendedFiltersSchema}
      fields={searchableFields}
      query={query}
      setQuery={setQuery}
      defaultQuery={defaultQuery}
      pageKey="pagina"
      defaultFieldName="oggetto"
      transform={semanticTagSearchableOptions.transform}
      extraActions={
        <>
          {
            // Mostra il pulsante di esportazione CSV solo se è abilitato lato chiavi
            FrontofficeExportFileLogic.showExportCsvButton && (
              <ExportSubmissionFileButton
                fileType={ExportableFileTypes.csv}
                query={query}
              />
            )
          }
          {
            // Mostra il pulsante di esportazione GeoJSON solo se è abilitato lato chiavi
            FrontofficeExportFileLogic.showExportGeojsonButton && (
              <ExportSubmissionFileButton
                fileType={ExportableFileTypes.geojson}
                query={query}
              />
            )
          }
        </>
      }
    />
  );

  return (
    <PageLayout
      extraButtons={
        isMobile && (
          <FilterDrawerButton
            open={openFilterDrawer}
            onOpen={() => setOpenFilterDrawer(true)}
            onClose={() => setOpenFilterDrawer(false)}
          >
            {filterform}
          </FilterDrawerButton>
        )
      }
    >
      <ResponsiveCard
        noPadding
        title={
          <TitleSubmissionRow
            id={PAGE_TITLE_ID}
            right={
              <Space size="small">
                <ArchivedSubmissionsLinkButton />
                {!isMobile && (
                  <FilterButton
                    id="filter-button-submission-list"
                    opened={openSearchPanel}
                    onClick={() => setOpenSearchPanel(!openSearchPanel)}
                  >
                    {t('submission-list.search', 'Ricerca')}
                  </FilterButton>
                )}
              </Space>
            }
          >
            {t('submission-list.submissions-list-title', 'Elenco Richieste')}
          </TitleSubmissionRow>
        }
      >
        <Divider style={{ margin: 0 }} />

        <FrontFilterPanel
          id="filter-panel-submission-list"
          buttonId="filter-button-submission-list"
          open={!isMobile && openSearchPanel}
        >
          {filterform}
        </FrontFilterPanel>

        <FrontMessageFilteredList
          visible={isFiltered}
          items={meta?.totale ?? null}
          message={t(
            'submission-list.domande-trovate-in-base-ai-filtri',
            'domande trovate in base ai filtri di ricerca.',
          )}
          action={
            <Button
              size="small"
              onClick={() => {
                setQuery(defaultQuery);
                returnFocusToPageTitle();
              }}
              children={t('submission-list.rimuovi-filtri', 'Rimuovi filtri')}
            />
          }
        />

        <TableData
          aria-label={t(
            'submission-list.submissions-list-title',
            'Elenco Richieste',
          )}
          showSorterTooltip={false}
          size="small"
          dataSource={submissions ?? []}
          loading={isLoading || isFetching}
          pagination={{
            current: Number(query.pagina ?? 0) + 1,
            pageSize: query.limite != null ? Number(query.limite) : 10,
            total: meta?.totale ?? 0,
            pageSizeOptions: [5, 10, 20, 50],
          }}
          onChange={onPaginationChange}
          columns={[
            ...columns,
            {
              title: (
                <span className="sr-only">
                  {t('submission-list.header-col.actions', 'Azioni')}
                </span>
              ),
              key: 'actions',
              align: 'right',
              fixed: isMobile ? undefined : 'right',
              render: (text, record) => (
                <Space>
                  <SubmissionDeleteButton submission={record} typeModal />
                  <SubmissionViewTableButton
                    record={record}
                    isMobile={isMobile}
                  />

                  {SubmissionLogic.canDuplicate(record, inquiries) && (
                    <Tooltip
                      title={t(
                        'submission-list.duplica-richiesta',
                        'Duplica richiesta',
                      )}
                    >
                      <SubmissionCloneButton
                        aria-label={t(
                          'submission-list.duplica-richiesta',
                          'Duplica richiesta',
                        )}
                        iconOnlyButton
                        type="link"
                        size="small"
                        submissionId={record.id}
                        isJustCreated={
                          record.stato === EnumRichiestaDTOStato.CREATA
                        }
                        icon={<CopyOutlined aria-hidden="true" />}
                      />
                    </Tooltip>
                  )}
                </Space>
              ),
            },
          ]}
        />
      </ResponsiveCard>
    </PageLayout>
  );
}
